import _ from 'lodash';
import React from 'react';

import {
  Archive,
  Assessment,
  Email,
  ImportExport,
  Storefront,
  SwapHoriz,
  Warehouse
} from '@mui/icons-material';
import {
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  styled
} from '@mui/material';
import {
  activityTemplates,
  BaseWidget,
  PermissionWall,
  routeDirectory,
  StoreFeatureToggle,
  useTabSearchParams,
  widgets
} from 'modules';
import {
  useNavigate,
  useParams
} from 'react-router-dom';

import { PermissionName } from 'generated/graphql';
import * as dataService from './dataService';



const ArchiveListItem = styled(ListItem)(({ theme }) => ({
  color: theme.palette.error.main,
  '& svg': {
    color: theme.palette.error.main
  }
}));



export const StoreSettingsActivity: React.FC = () => {
  const params = useParams<{ storeId: string; }>();
  const [tabId, setTabId] = useTabSearchParams({
    paramName: 'tabId',
    defaultTabId: 'erpSettings'
  });
  const queryTuple = dataService.useQuery(params.storeId ?? '');
  const [archiveStore, archiveStoreTuple] = dataService.useArchiveStore(params.storeId ?? '');
  const navigate = useNavigate();



  function handleClickTab(tabId: string) {
    setTabId(tabId);
  }



  async function handleClickArchiveStore() {
    const text = _.join([
      `Are you sure you want to archive this store?.`,
      `It will no longer show up in any lists, or process orders from the source.`
    ], '\n\n');

    if (window.confirm(text)) {
      await archiveStore();
      window.alert("Store archived successfully.");
      navigate(routeDirectory.admin.root.path(), {
        replace: true
      });
    }
  }



  return !params.storeId ? null : (
    <activityTemplates.AdminActivityTemplate.FC>
      <Grid
        container
        spacing={2}>
        <Grid
          item
          xs={2}>
          <List>
            {_.map(__tabs, tab => {
              const elContent = (
                <ListItem key={tab.id}>
                  <ListItemButton
                    selected={tabId === tab.id}
                    onClick={_.partial(handleClickTab, tab.id)}>
                    <ListItemIcon>{tab.elIcon}</ListItemIcon>

                    <ListItemText primary={tab.label} />
                  </ListItemButton>
                </ListItem>
              );

              return !_.isEmpty(tab.requiredPermissions) ? (
                <PermissionWall.FC
                  key={tab.id}
                  storeId={params.storeId}
                  requiresPermissions={tab.requiredPermissions}>
                  {elContent}
                </PermissionWall.FC>
              ) : elContent;
            })}

            <PermissionWall.FC requiresPermissions={['store_archive']}>
              <Divider />

              <ArchiveListItem>
                <ListItemButton
                  disabled={archiveStoreTuple.loading}
                  onClick={handleClickArchiveStore}>
                  <ListItemIcon>
                    <Archive />
                  </ListItemIcon>

                  <ListItemText primary="Archive Store" />
                </ListItemButton>
              </ArchiveListItem>
            </PermissionWall.FC>
          </List>
        </Grid>

        <Grid
          container
          item
          xs={10}
          spacing={2}>
          {tabId === 'brand' && (
            <>
              <Grid
                item
                xs={12}
                md={6}>
                <widgets.store.BrandSettingsWidget.FC storeId={params.storeId} />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}>
                <widgets.store.StoreReferralsWidget.FC storeId={params.storeId} />
              </Grid>
            </>
          )}

          {tabId === 'erpSettings' && (
            <PermissionWall.FC requiresPermissions={['store_updateErpSettings']}>
              <Grid
                item
                xs={12}
                md={6}>
                <widgets.store.NetSuiteSettingsWidget.FC storeId={params.storeId} />
              </Grid>

              <Grid
                item
                xs={12}
                md={12}>
                <widgets.store.ShippingServiceWidget.FC storeId={params.storeId} />
              </Grid>

              <Grid
                item
                xs={12}
                md={12}>
                <widgets.store.PaymentMethodWidget.FC storeId={params.storeId} />
              </Grid>
            </PermissionWall.FC>
          )}

          {tabId === 'importSettings' && (
            <>
              {queryTuple.data.storeType === 'customizableFormat' && (
                <Grid
                  item
                  xs={12}
                  md={12}>
                  <widgets.store.CsvOrderFormatSettings.FC storeId={params.storeId} />
                </Grid>
              )}

              <Grid
                item
                xs={12}
                md={12}>
                <widgets.store.LineItemUnbundlerWidget.FC storeId={params.storeId} />
              </Grid>
            </>
          )}

          {tabId === 'reporting' && (
            <>
              <PermissionWall.FC
                storeId={params.storeId}
                requiresPermissions={[
                  'store_viewAsnSettings',
                  'store_updateAsnSettings'
                ]}>
                <Grid
                  item
                  xs={12}
                  md={12}>
                  <widgets.store.AsnSettingsWidget.FC storeId={params.storeId ?? ''} />
                </Grid>
              </PermissionWall.FC>
              <PermissionWall.FC
                storeId={params.storeId}
                requiresPermissions={['reporting_updateAutomatedReportRecipient']}>
                <Grid
                  item
                  xs={12}
                  md={4}>
                  <widgets.store.AutomatedEmailReportSettingsWidget.FC storeId={params.storeId ?? ''} />
                </Grid>
              </PermissionWall.FC>

              <Grid
                item
                xs={6}
                md={4}>
                <BaseWidget.FC title="Inventory">
                  <StoreFeatureToggle.FC
                    featureName="reportInventoryLevels"
                    label="Inventory Management"
                    storeId={params.storeId} />
                </BaseWidget.FC>
              </Grid>

              <Grid
                item
                xs={6}
                md={4}>
                <BaseWidget.FC title="Price">
                  <StoreFeatureToggle.FC
                    featureName="reportPriceLevels"
                    label="Price Management"
                    storeId={params.storeId} />
                </BaseWidget.FC>
              </Grid>
            </>
          )}

          {tabId === 'emailSettings' && (
            <>
              <PermissionWall.FC
                storeId={params.storeId}
                requiresPermissions={['store_updateEmailSettings']}>
                <Grid
                  item
                  xs={12}
                  md={6}>
                  <Stack spacing={2}>
                    <widgets.store.EmailSettingsWidget.FC storeId={params.storeId} />

                    <widgets.store.CustomFieldsWidget.FC storeId={params.storeId} />
                  </Stack>
                </Grid>
              </PermissionWall.FC>

              <Grid
                item
                xs={12}
                md={6}>
                <Stack spacing={3}>
                  <widgets.store.DynamicDataExampleWidget.FC storeId={params.storeId} />
                </Stack>
              </Grid>
            </>
          )}

          {tabId === 'returns' && (
            <>
              <Grid
                item
                xs={12}
                md={6}>
                {queryTuple.data.storeType === 'cleoTier1' && (
                  <PermissionWall.FC
                    storeId={params.storeId}
                    requiresPermissions={[
                      'tier1_saveTriageWorkflow',
                      'tier1_viewTriageWorkflow'
                    ]}>
                    <widgets.store.TriageWorkflowsWidget.FC storeId={params.storeId} />
                  </PermissionWall.FC>
                )}
              </Grid>

              <Grid
                item
                xs={12}
                md={6}>
                {queryTuple.data.storeType === 'cleoTier1' && (
                  <PermissionWall.FC
                    storeId={params.storeId}
                    requiresPermissions={[
                      'tier1_saveTriageReason',
                      'tier1_viewTriageReason'
                    ]}>
                    <widgets.store.TriageReasonsWidget.FC storeId={params.storeId} />
                  </PermissionWall.FC>
                )}
              </Grid>

              <Grid
                item
                xs={12}
                md={6}>
                {queryTuple.data.storeType === 'cleoTier1' && (
                  <PermissionWall.FC
                    storeId={params.storeId}
                    requiresPermissions={['tier1_savePrintRequestSettings']}>
                    <widgets.store.ReturnLabelSettingsWidget.FC storeId={params.storeId} />
                  </PermissionWall.FC>
                )}
              </Grid>

              <Grid
                item
                xs={12}
                md={6}>
                {queryTuple.data.storeType === 'cleoTier1' && (
                  <PermissionWall.FC
                    storeId={params.storeId}
                    requiresPermissions={[
                      'tier1_saveRepairCode',
                      'tier1_viewRepairCode'
                    ]}>
                    <widgets.store.RepairCodesWidget.FC storeId={params.storeId} />
                  </PermissionWall.FC>
                )}
              </Grid>

              <Grid
                item
                xs={12}
                md={6}>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}>
                {queryTuple.data.storeType === 'cleoTier1' && (
                  <PermissionWall.FC
                    storeId={params.storeId}
                    requiresPermissions={[
                    ]}>
                    <widgets.store.SoftwareVersionsWidget.FC storeId={params.storeId} />
                  </PermissionWall.FC>
                )}
              </Grid>

            </>
          )}
        </Grid>
      </Grid>
    </activityTemplates.AdminActivityTemplate.FC>
  );
};



interface ITab {
  id: string;
  label: string;
  elIcon: JSX.Element;
  requiredPermissions: PermissionName[];
}
const __tabs: ITab[] = [
  {
    id: 'erpSettings',
    label: "ERP Settings",
    elIcon: <Warehouse />,
    requiredPermissions: ['store_updateErpSettings']
  },
  {
    id: 'brand',
    label: "Brand",
    elIcon: <Storefront />,
    requiredPermissions: ['store_updateBrand']
  },
  {
    id: 'importSettings',
    label: "Import Settings",
    elIcon: <ImportExport />,
    requiredPermissions: ['store_updateImportSettings']
  },
  {
    id: 'reporting',
    label: "Reporting",
    elIcon: <Assessment />,
    requiredPermissions: [
      'store_viewAsnSettings',
      'store_updateAsnSettings',
      'reporting_updateAutomatedReportRecipient'
    ]
  },
  {
    id: 'emailSettings',
    label: "Customer Emails",
    elIcon: <Email />,
    requiredPermissions: ['store_viewEmailSettings']
  },
  {
    id: 'returns',
    label: "Returns",
    elIcon: <SwapHoriz />,
    requiredPermissions: []
  }
];
